import { LoadingSpinner } from "@rothoblaas/design-system";
import { FC, PropsWithChildren, useEffect } from "react";
import { useAuthContext } from "../context/authContext";
import useI18n from "../hooks/useTranslations";
import AccessDeniedPage from "./AccessDeniedPage";
import styles from "./AccessGate.module.css";

type AccessGateProps = PropsWithChildren<{
  // Send anonymous visitors to the login page and hold the children back until the identity is
  // known. Off for pages that also work anonymously (the invite pages).
  requireLogin?: boolean;
}>;

/**
 * Bootstraps the session and decides whether this app may be shown at all. Shared by the main app
 * and the single-module apps: the backend's access check is scoped to the current app (see
 * AppContext.scope), so a user blocked from, or locked out of, this app gets the access-denied page
 * and never sees any of its content.
 */
const AccessGate: FC<AccessGateProps> = ({ requireLogin = false, children }) => {
  const { init, identity, isLoading, accessDenied } = useAuthContext();
  const i18n = useI18n();

  useEffect(() => {
    init(requireLogin);
    if (import.meta.env.MODE === "staging" && !document.title.includes(i18n.translation.common.testEnvironment)) {
      document.title += " - " + i18n.translation.common.testEnvironment;
    }
  }, []);

  if (accessDenied) {
    return <AccessDeniedPage />;
  }

  if (requireLogin) {
    if (isLoading) {
      return (
        <div className={styles.loadingSpinner}>
          <LoadingSpinner />
        </div>
      );
    }
    if (!identity) {
      return null;
    }
  }

  return <>{children}</>;
};

export default AccessGate;
